import { useUserRole } from '@/hooks/useUserRole';
import { Navigate, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import LoadingSpinner from '@/components/LoadingSpinner';
import { ClientManagement } from '@/components/practitioner/ClientManagement';
import { ClientSessionCreator } from '@/components/practitioner/ClientSessionCreator';
import { PractitionerAllSessionsPage } from '@/pages/PractitionerAllSessionsPage';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Activity, TrendingUp, Calendar, Clock, Link, ExternalLink, Eye, MessageSquare, Plus, UserPlus, Share2, BarChart3 } from 'lucide-react';
import { usePractitionerClients } from '@/hooks/usePractitionerClients';
import { usePractitionerSessions } from '@/hooks/usePractitionerSessions';
import { useMemo, useState } from 'react';
import { format, isThisMonth } from 'date-fns';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { PractitionerSidebar } from '@/components/practitioner/PractitionerSidebar';
import { ReferralDashboard } from '@/components/practitioner/ReferralDashboard';
import { CreditBalance } from '@/components/practitioner/CreditBalance';
import { PractitionerCreditsPage } from '@/pages/PractitionerCreditsPage';
import { PractitionerPerksPage } from '@/pages/PractitionerPerksPage';
import { PractitionerSubscriptionPage } from '@/pages/PractitionerSubscriptionPage';
import { NotificationPreferencesPage } from '@/pages/NotificationPreferencesPage';
import PractitionerShortsGenerator from '@/components/practitioner/PractitionerShortsGenerator';
import { Breadcrumbs } from '@/components/Breadcrumbs';

const DashboardOverview = () => {
  const navigate = useNavigate();
  const { clients, loading: clientsLoading } = usePractitionerClients();
  const { clientSessions, sharedLinks, loading: sessionsLoading } = usePractitionerSessions();
  const [recentActivityOpen, setRecentActivityOpen] = useState(true);

  const metrics = useMemo(() => {
    const thisMonthSessions = clientSessions.filter(session =>
      isThisMonth(new Date(session.created_at))
    );

    // Links never expire now, so just check if active
    const activeSharedLinks = sharedLinks.filter(link => link.is_active);

    const pendingSessions = sharedLinks.filter(link =>
      link.is_active &&
      (link.delivery_status === 'created' || link.delivery_status === 'sent')
    );

    const recentActivity = [...clientSessions]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 5);

    const sessionsWithFeedback = clientSessions.filter(session => {
      if (!session.client_feedback) return false;
      if (typeof session.client_feedback === 'string') {
        return (session.client_feedback as string).trim().length > 0;
      }
      if (typeof session.client_feedback === 'object' && session.client_feedback !== null) {
        return Object.keys(session.client_feedback).length > 0;
      }
      return false;
    });

    return {
      totalClients: clients.length,
      sessionsThisMonth: thisMonthSessions.length,
      activeSharedLinks: activeSharedLinks.length,
      pendingSessions: pendingSessions.length,
      sessionsWithFeedback: sessionsWithFeedback.length,
      recentActivity
    };
  }, [clients, clientSessions, sharedLinks]);

  if (clientsLoading || sessionsLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Dashboard Overview</h1>
        <p className="text-muted-foreground">
          Track your practice metrics and recent activity
        </p>
      </div>

      {/* Quick Actions Section */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-blue-500" onClick={() => navigate('/practitioner/sessions')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Create Session</CardTitle>
              <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <Plus className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Generate personalized sessions for your clients</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-green-500" onClick={() => navigate('/practitioner/clients')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Manage Clients</CardTitle>
              <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <UserPlus className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Add or edit client information</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-purple-500" onClick={() => navigate('/practitioner/referrals')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Get Referrals</CardTitle>
              <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                <Share2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Share your referral code and earn rewards</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-amber-500" onClick={() => navigate('/practitioner/analytics')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">View Analytics</CardTitle>
              <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Track engagement and session stats</p>
          </CardContent>
        </Card>
      </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5 mb-8">
          <CreditBalance compact={true} />
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Clients</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.totalClients}</div>
              <p className="text-xs text-muted-foreground">
                {metrics.totalClients === 0 ? 'No clients yet' : 'Active clients'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Sessions This Month</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.sessionsThisMonth}</div>
              <p className="text-xs text-muted-foreground">
                {metrics.sessionsThisMonth === 0 ? 'No sessions yet' : 'Created this month'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Links</CardTitle>
              <Link className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.activeSharedLinks}</div>
              <p className="text-xs text-muted-foreground">
                {metrics.activeSharedLinks === 0 ? 'No active links' : 'Shared session links'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Client Feedback</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.sessionsWithFeedback}</div>
              <p className="text-xs text-muted-foreground">
                {metrics.sessionsWithFeedback === 0 ? 'No feedback yet' : 'sessions with feedback'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Sessions</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.pendingSessions}</div>
              <p className="text-xs text-muted-foreground">
                {metrics.pendingSessions === 0 ? 'All sessions accessed' : 'Awaiting client access'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity Section */}
        {metrics.recentActivity.length > 0 && (
          <Collapsible 
            open={recentActivityOpen} 
            onOpenChange={setRecentActivityOpen} 
            className="mb-8"
          >
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Recent Activity</CardTitle>
                    <CardDescription>Latest sessions created for your clients</CardDescription>
                  </div>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-9 p-0">
                      {recentActivityOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                      <span className="sr-only">Toggle recent activity</span>
                    </Button>
                  </CollapsibleTrigger>
                </div>
              </CardHeader>
              <CollapsibleContent>
                <CardContent>
                  <div className="space-y-3">
                    {metrics.recentActivity.map((session) => {
                      const sharedLink = sharedLinks.find(link => link.session_id === session.id);
                      const client = clients.find(c => c.id === session.client_id);
                      
                      return (
                        <div key={session.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{session.title}</p>
                            <p className="text-xs text-muted-foreground">
                              Client: {client?.client_name || 'Unknown'} • {format(new Date(session.created_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {sharedLink && sharedLink.is_active ? (
                              <Badge variant="secondary" className="text-xs">
                                <Eye className="w-3 h-3 mr-1" />
                                {sharedLink.delivery_status === 'accessed' ? 'Accessed' : 'Pending'}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">No Link</Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
    </div>
  );
};

const SessionLinksView = () => {
  const { clients } = usePractitionerClients();
  const { clientSessions, sharedLinks } = usePractitionerSessions();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Session Links</h1>
        <p className="text-muted-foreground">
          Monitor and manage shared session links for your clients
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          {sharedLinks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                No shared session links yet. Create sessions to generate shareable links.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {sharedLinks.map((link) => {
                const session = clientSessions.find(s => s.id === link.session_id);
                const client = clients.find(c => c.id === link.client_id);

                return (
                  <div key={link.id} className="meditation-card p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-medium">{session?.title || 'Unknown Session'}</h4>
                          <Badge variant={link.is_active ? "default" : "secondary"}>
                            {link.is_active ? link.delivery_status || 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">
                          Client: {client?.client_name || 'Unknown'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {link.created_at && (
                            <>Created: {format(new Date(link.created_at), 'MMM d, yyyy h:mm a')} • </>
                          )}
                          Plays: {link.total_plays}
                        </p>
                        {link.last_accessed_at && (
                          <p className="text-xs text-muted-foreground">
                            Last accessed: {format(new Date(link.last_accessed_at), 'MMM d, yyyy h:mm a')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {link.is_active && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const url = `${window.location.origin}/listen/${encodeURIComponent(link.access_token)}`;
                              navigator.clipboard.writeText(url);
                            }}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Copy Link
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AnalyticsView = () => {
  const { clients } = usePractitionerClients();
  const { clientSessions, sharedLinks } = usePractitionerSessions();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Analytics</h1>
        <p className="text-muted-foreground">
          Track session creation patterns and client engagement
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Session Analytics</CardTitle>
            <CardDescription>Session creation and access patterns</CardDescription>
          </CardHeader>
          <CardContent>
            {clientSessions.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No session data available yet.</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{clientSessions.length}</div>
                    <p className="text-xs text-muted-foreground">Sessions Created</p>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {sharedLinks.length}
                    </div>
                    <p className="text-xs text-muted-foreground">Sessions Shared</p>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">
                      {sharedLinks.filter(l => l.access_count > 0).length}
                    </div>
                    <p className="text-xs text-muted-foreground">Sessions Accessed</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-amber-600">
                      {clientSessions.length - sharedLinks.length}
                    </div>
                    <p className="text-xs text-muted-foreground">🔄 Generated Only</p>
                    <span className="text-xs text-amber-600">(Need Step 2)</span>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-emerald-600">
                      {Math.round((sharedLinks.filter(l => l.access_count > 0).length / Math.max(sharedLinks.length, 1)) * 100)}%
                    </div>
                    <p className="text-xs text-muted-foreground">📊 Access Rate</p>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Session Categories</h4>
                  <div className="space-y-2">
                    {Object.entries(
                      clientSessions.reduce((acc, session) => {
                        acc[session.category_id] = (acc[session.category_id] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>)
                    ).map(([category, count]) => (
                      <div key={category} className="flex justify-between text-sm">
                        <span className="capitalize">{category.replace('_', ' ')}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Client Engagement</CardTitle>
            <CardDescription>Client activity and session access</CardDescription>
          </CardHeader>
          <CardContent>
            {clients.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No client data available yet.</p>
            ) : (
              <div className="space-y-3">
                {clients.map(client => {
                  const clientSessionsForClient = clientSessions.filter(s => s.client_id === client.id);
                  const accessedSessions = sharedLinks.filter(l => 
                    l.client_id === client.id && l.access_count > 0
                  ).length;
                  
                  return (
                    <div key={client.id} className="flex justify-between items-center p-3 bg-muted/20 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{client.client_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {clientSessionsForClient.length} created • {accessedSessions} accessed
                    </p>
                    {clientSessionsForClient.length > accessedSessions && (
                      <p className="text-xs text-amber-600">
                        {clientSessionsForClient.length - accessedSessions} not accessed yet
                      </p>
                    )}
                  </div>
                      <Badge variant="outline" className="text-xs">
                        {accessedSessions} accessed
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const PractitionerDashboard = () => {
  const { hasPractitionerAccess, loading } = useUserRole();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!hasPractitionerAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <PractitionerSidebar />
        
        <div className="flex-1 flex flex-col">
          <header className="h-14 flex items-center border-b border-border px-6">
            <SidebarTrigger />
          </header>
          
          <main className="flex-1 p-6">
            <Breadcrumbs />
            <Routes>
              <Route path="/" element={<DashboardOverview />} />
              <Route path="credits" element={<PractitionerCreditsPage />} />
              <Route path="perks" element={<PractitionerPerksPage />} />
              <Route path="subscription" element={<Navigate to="/practitioner/credits?tab=subscription" replace />} />
              <Route path="shorts" element={<PractitionerShortsGenerator />} />
              <Route path="clients" element={<ClientManagement />} />
              <Route path="sessions" element={<ClientSessionCreator />} />
              <Route path="all-sessions" element={<PractitionerAllSessionsPage />} />
              <Route path="links" element={<SessionLinksView />} />
              <Route path="referrals" element={<ReferralDashboard />} />
              <Route path="notifications" element={<NotificationPreferencesPage />} />
              <Route path="analytics" element={<AnalyticsView />} />
            </Routes>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};